import React from 'react';
import type { FC } from 'react';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { Box, Link, PointerSmallIcon, Text } from '@wix/cli-ui-kit';
import { PlatformType } from '@wix/cli-app-definitions';
import { useExtensions } from '../extensions/useExtensions.js';
import {
  useResolveDashboardExtensionUrl,
  useResolvePlatformUrl,
} from '../extensions/utils.js';
import { usePlatform } from './usePlatform.js';
import { useSite } from '../../site/useSite.js';

interface PlatformLinkProps {
  url: string;
  label: string;
  withPointer?: boolean;
}

const PlatformLink: FC<PlatformLinkProps> = ({
  url,
  label,
  withPointer = true,
}) => {
  return (
    <Box>
      <Box marginRight={1}>
        {withPointer && <PointerSmallIcon />}
        <Box marginLeft={1}>
          <Link url={url}>{label}</Link>
        </Box>
      </Box>
    </Box>
  );
};

export const PlatformViewer: FC = () => {
  const { t } = useTranslation();
  const { isPlatformExists, hasSinglePlatform } = usePlatform();
  const { dashboardExtensions } = useExtensions();
  const { site } = useSite();
  const resolvePlatformUrl = useResolvePlatformUrl();
  const resolveDashboardExtensionUrl = useResolveDashboardExtensionUrl();

  return (
    <>
      {hasSinglePlatform(PlatformType.Dashboard()) ? (
        dashboardExtensions[0] && (
          <Box flexDirection="row">
            <Trans i18nKey="general.open_preview.title_single" />
            <PlatformLink
              withPointer={false}
              url={resolveDashboardExtensionUrl(dashboardExtensions[0])}
              label={t('general.platform.dashboard')}
            />
          </Box>
        )
      ) : (
        <Box flexDirection="column">
          <Trans i18nKey="general.open_preview.title_multiple" />
          <Box paddingLeft={2} flexDirection="column">
            {isPlatformExists(PlatformType.Site()) &&
              (site.viewUrl ? (
                <PlatformLink
                  url={resolvePlatformUrl(PlatformType.Site())}
                  label={t('general.platform.site')}
                />
              ) : (
                <Box marginRight={1}>
                  <PointerSmallIcon />
                  <Box marginLeft={1}>
                    <Text skin="secondary" underline>
                      {t('general.platform.unpublished_site')}
                    </Text>
                  </Box>
                </Box>
              ))}
            {isPlatformExists(PlatformType.Editor()) && (
              <PlatformLink
                url={resolvePlatformUrl(PlatformType.Editor())}
                label={t('general.platform.editor')}
              />
            )}
            {isPlatformExists(PlatformType.Dashboard()) && (
              <PlatformLink
                url={resolveDashboardExtensionUrl(dashboardExtensions[0]!)}
                label={t('general.platform.dashboard')}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
};
